import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { MerchantView } from '@/api/types';
import { Card, ErrorNote, Money, Reference, Spinner, Timestamp } from '@/components/Common';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/format';

interface ReconciliationLine {
  currency: string;
  collected: string;
  dispensed: string;
  refunded: string;
  commission: string;
  variance: string;
  balanced: boolean;
}

interface SettlementReport {
  merchantCode: string;
  merchantName: string;
  date: string;
  rechargesByProduct: Array<{
    product: string; status: string; currency: string;
    orderCount: number; total: string; commission: string;
  }>;
  paymentsByMethod: Array<{
    method: string; status: string; currency: string; paymentCount: number; total: string;
  }>;
  reconciliation: ReconciliationLine[];
}

interface PendingRefund {
  id: string; orderId: string; agentReference?: string; amount: string;
  currency?: string; reason: string; createdAt: string;
}

export function SettlementPage() {
  const { can } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [merchantId, setMerchantId] = useState('');
  const [exporting, setExporting] = useState(false);

  async function exportCsv() {
    if (!merchantId) return;
    setExporting(true);
    try {
      // A plain <a href> wouldn't carry the bearer token (the access token lives in memory, not a
      // cookie) — fetch it through the authenticated client and hand the browser a blob instead.
      const csv = await api.getRaw(`/api/admin/settlement/merchants/${merchantId}/csv?date=${date}`);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `settlement-${merchantId}-${date}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  const { data: merchants } = useQuery({
    queryKey: ['merchants'],
    queryFn: () => api.get<{ merchants: MerchantView[] }>('/api/admin/merchants'),
  });

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['settlement', merchantId, date],
    queryFn: () => api.get<SettlementReport>(
      `/api/admin/settlement/merchants/${merchantId}?date=${date}`),
    enabled: Boolean(merchantId),
  });

  return (
    <div className="space-y-4">
      {can('ADMIN', 'FINANCE', 'OPS') && <PendingRefunds />}

      <Card
        title="Settlement"
        actions={
          <div className="flex gap-2">
            <select className="input w-56" value={merchantId}
                    onChange={(e) => setMerchantId(e.target.value)}>
              <option value="">Choose a merchant…</option>
              {(merchants?.merchants ?? []).map((merchant) => (
                <option key={merchant.id} value={merchant.id}>
                  {merchant.code} — {merchant.name}
                </option>
              ))}
            </select>
            <input className="input w-40" type="date" value={date}
                   onChange={(e) => setDate(e.target.value)} />
            {merchantId && (
              <button type="button" className="btn-secondary" disabled={exporting}
                      onClick={() => void exportCsv()}>
                {exporting ? 'Exporting…' : 'CSV'}
              </button>
            )}
          </div>
        }
      >
        {!merchantId && (
          <p className="px-4 py-10 text-center text-sm text-ink-500">
            Choose a merchant and a date.
          </p>
        )}
        {isLoading && <Spinner />}
        {error && <ErrorNote error={error} />}

        {report && (
          <div className="space-y-6 p-4">
            <div>
              <h3 className="text-sm font-semibold text-ink-800">
                Reconciliation — collected minus dispensed minus refunded
              </h3>
              <table className="mt-2 w-full">
                <thead className="border-b border-ink-200">
                  <tr>
                    <th className="th">Currency</th>
                    <th className="th text-right">Collected</th>
                    <th className="th text-right">Dispensed</th>
                    <th className="th text-right">Refunded</th>
                    <th className="th text-right">Commission</th>
                    <th className="th text-right">Variance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {report.reconciliation.map((line) => (
                    <tr key={line.currency} className={line.balanced ? '' : 'bg-red-50'}>
                      <td className="td font-medium">{line.currency}</td>
                      <td className="td text-right"><Money amount={money(line.collected)} /></td>
                      <td className="td text-right"><Money amount={money(line.dispensed)} /></td>
                      <td className="td text-right"><Money amount={money(line.refunded)} /></td>
                      <td className="td text-right"><Money amount={money(line.commission)} /></td>
                      <td className={`td text-right font-semibold ${
                        line.balanced ? 'text-emerald-700' : 'text-red-700'
                      }`}>
                        <Money amount={money(line.variance)} />
                      </td>
                    </tr>
                  ))}
                  {report.reconciliation.length === 0 && (
                    <tr><td className="td text-ink-500" colSpan={6}>
                      Nothing moved on this date.
                    </td></tr>
                  )}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-ink-500">
                A non-zero variance means the money collected and the value dispensed disagree —
                better found here than at month end.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-ink-800">Recharges by product</h3>
              <table className="mt-2 w-full">
                <thead className="border-b border-ink-200">
                  <tr>
                    <th className="th">Product</th><th className="th">Status</th>
                    <th className="th">Currency</th><th className="th text-right">Orders</th>
                    <th className="th text-right">Value</th><th className="th text-right">Commission</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {report.rechargesByProduct.map((row, index) => (
                    <tr key={index}>
                      <td className="td">{row.product}</td>
                      <td className="td text-ink-600">{row.status}</td>
                      <td className="td text-ink-500">{row.currency}</td>
                      <td className="td tabular text-right">{row.orderCount}</td>
                      <td className="td text-right"><Money amount={money(row.total)} /></td>
                      <td className="td text-right"><Money amount={money(row.commission)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-ink-800">Payments collected</h3>
              <table className="mt-2 w-full">
                <thead className="border-b border-ink-200">
                  <tr>
                    <th className="th">Method</th><th className="th">Status</th>
                    <th className="th">Currency</th><th className="th text-right">Count</th>
                    <th className="th text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {report.paymentsByMethod.map((row, index) => (
                    <tr key={index}>
                      <td className="td">{row.method}</td>
                      <td className="td text-ink-600">{row.status}</td>
                      <td className="td text-ink-500">{row.currency}</td>
                      <td className="td tabular text-right">{row.paymentCount}</td>
                      <td className="td text-right"><Money amount={money(row.total)} /></td>
                    </tr>
                  ))}
                  {report.paymentsByMethod.length === 0 && (
                    <tr><td className="td text-ink-500" colSpan={5}>
                      No payments recorded — this merchant may trade off a prepaid float.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function PendingRefunds() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['refunds'],
    queryFn: () => api.get<{ refunds: PendingRefund[] }>('/api/admin/settlement/refunds'),
  });

  const complete = useMutation({
    mutationFn: (refundId: string) =>
      api.post(`/api/admin/settlement/refunds/${refundId}/complete`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['refunds'] }),
  });

  const refunds = data?.refunds ?? [];
  if (refunds.length === 0) return null;

  return (
    <Card title={`Refunds awaiting confirmation — ${refunds.length}`}>
      <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-ink-200">
          <tr>
            <th className="th">Order</th><th className="th text-right">Amount</th>
            <th className="th">Reason</th><th className="th">Opened</th><th className="th" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {refunds.map((refund) => (
            <tr key={refund.id}>
              <td className="td">
                <Link to={`/orders/${refund.orderId}`} className="hover:underline">
                  <Reference value={refund.agentReference ?? refund.orderId} />
                </Link>
              </td>
              <td className="td text-right">
                <Money amount={money(refund.amount)} currency={refund.currency} />
              </td>
              <td className="td max-w-md truncate text-ink-600">{refund.reason}</td>
              <td className="td"><Timestamp value={refund.createdAt} /></td>
              <td className="td text-right">
                {can('ADMIN', 'FINANCE') && (
                  <button type="button" className="btn-secondary" disabled={complete.isPending}
                          onClick={() => complete.mutate(refund.id)}>
                    Confirm paid out
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {complete.isError && <ErrorNote error={complete.error} />}
      <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
        A refund never completes itself. Confirming here asserts the customer actually has their
        money back, under your name.
      </p>
    </Card>
  );
}
